/**
 * Per-app private connections — list + create + delete. Manager-only for
 * writes; the tab is manager-only anyway, but a non-manager who pastes the
 * URL gets a read-only notice (defense in depth on top of the server's 403).
 *
 * A connection lets an app reach a private HTTP service running on the
 * user's own server or VM. Creating one returns a one-time install command
 * (a `curl … | sudo sh … --token=…` one-liner); the token is shown ONLY at
 * creation and is never returned by GET — recreate the connection if lost.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, apiMessage } from '../api/client.js';
import { AppTabs } from '../components/AppTabs.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { IconPlug, IconTrash } from '../components/Icons.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  CopyButton,
  EmptyState,
  Field,
  Mono,
  PageHeader,
  PageSkeleton,
  inputClass,
} from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith } from '../lib/useAppPage.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';
import type {
  ConnectionSummary,
  ConnectionStatusResult,
  ConnectionTestResult,
} from '../api/types.js';

interface CreatedConnection {
  internalHost: string;
  installCommand: string;
}

/**
 * Connector teardown command, shown at the delete step (confirm) and echoed by
 * the server for the post-delete banner. Uses the absolute install path so it
 * resolves under Linux's sudo `secure_path` (a bare `cloudflared` is
 * command-not-found there) — see the matching note in control-plane
 * connections.ts.
 */
const CONNECTOR_UNINSTALL_CMD = 'sudo /usr/local/bin/cloudflared service uninstall';

/**
 * The wire-up snippet a user copies and pastes to the in-browser AI so it knows
 * exactly WHERE and HOW to call a private connection. Without this the agent
 * guesses the call site — most often `env.KIN.connection(...)`, which doesn't
 * exist and fails at runtime. So the copied text is the instructions, not just
 * a one-liner: it names the two valid call paths (the `KinApp` subclass helper
 * `this.connection`, and the plain-`fetch`-handler RPC fallback) and the
 * anti-pattern. Mirrors docs/skill/kin-skill.md.
 */
function connectionWireup(slug: string): string {
  return [
    `Wire up the "${slug}" private connection in this app.`,
    ``,
    'If the app is a KinApp subclass (`export default class extends KinApp` — the default',
    'scaffold), call it from inside any method. `this.connection` is a KinApp method:',
    ``,
    `  const res = await this.connection("${slug}").fetch("/path");`,
    '  const data = await res.json();',
    ``,
    'The SDK resolves the host and stamps the Cloudflare Access headers for you — the app',
    'never handles the credentials. Treat it like a scoped fetch to the upstream service.',
    ``,
    'If instead the app is a plain fetch handler (`export default { async fetch(request, env) }`,',
    'so there is no `this`), resolve the connection over the RPC binding and stamp the headers',
    'yourself:',
    ``,
    `  const c = await env.KIN.getConnection(env.KIN_APP_ID, "${slug}"); // { host, clientId, clientSecret }`,
    '  const res = await fetch(`https://${c.host}/path`, {',
    '    headers: {',
    '      "CF-Access-Client-Id": c.clientId,',
    '      "CF-Access-Client-Secret": c.clientSecret,',
    '    },',
    '  });',
    ``,
    `Never use env.KIN.connection("${slug}") — that method does not exist on the binding and`,
    'fails at runtime with `The RPC receiver does not implement the method "connection"`.',
  ].join('\n');
}

export function Connections() {
  const { slug } = useParams();
  const { app, payload, loading, error, reload, setPayload } = useAppPageWith(slug, (s) =>
    api.get<{ connections: ConnectionSummary[] }>(`/api/apps/${encodeURIComponent(s)}/connections`),
  );
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [origin, setOrigin] = useState('http://localhost:8080');
  const [created, setCreated] = useState<CreatedConnection | null>(null);
  const [uninstall, setUninstall] = useState<string | null>(null);
  const [teardownFailures, setTeardownFailures] = useState<string[] | null>(null);
  // Per-slug origin reachability from the latest poll. Kept out of
  // ConnectionSummary (which mirrors the wire shape) so it stays a pure
  // client-side hint. `false` => tunnel up but the app behind it didn't answer.
  const [originOk, setOriginOk] = useState<Record<string, boolean>>({});
  const [testOpen, setTestOpen] = useState<string | null>(null); // slug with an open tester
  const [testPath, setTestPath] = useState('/');
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [testing, setTesting] = useState(false);

  // Poll the status endpoint for any connection that isn't Connected yet —
  // `pending` right after create, or `down` while the connector hasn't reached
  // Cloudflare's edge. This refreshes the badge without a manual reload AND
  // recovers the common case where the connector comes up AFTER an initial
  // `down` was recorded (a freshly-installed connector that the first poll
  // caught before it connected). Stops once a connection flips to `connected`.
  //
  // Re-subscribe only when the *set of not-yet-connected slugs* changes —
  // merging a connected status removes that slug from the set, winding the
  // interval down, without churning the effect for already-connected rows.
  const unsettledSlugs = useMemo(
    () =>
      (payload?.connections ?? [])
        .filter((c) => c.status === 'pending' || c.status === 'down')
        .map((c) => c.slug)
        .sort()
        .join(','),
    [payload?.connections],
  );
  const inFlight = useRef(false);
  useEffect(() => {
    if (!slug || unsettledSlugs === '') return;
    inFlight.current = false;
    const slugs = unsettledSlugs.split(',');
    const poll = async () => {
      if (inFlight.current) return; // skip if the prior cycle is still running
      inFlight.current = true;
      try {
        await Promise.all(
          slugs.map(async (connSlug) => {
            const res = await api.get<ConnectionStatusResult>(
              `/api/apps/${encodeURIComponent(slug)}/connections/${encodeURIComponent(connSlug)}/status`,
            );
            setPayload((prev) => {
              // Skip the state churn (and re-render) when nothing changed —
              // a stuck-`down` connection polls every 4s indefinitely.
              const cur = prev.connections.find((c) => c.slug === connSlug);
              if (cur && cur.status === res.status && cur.lastSeenAt === res.lastSeenAt) {
                return prev;
              }
              return {
                connections: prev.connections.map((c) =>
                  c.slug === connSlug
                    ? { ...c, status: res.status, lastSeenAt: res.lastSeenAt }
                    : c,
                ),
              };
            });
            setOriginOk((prev) =>
              prev[connSlug] === res.originOk ? prev : { ...prev, [connSlug]: res.originOk },
            );
          }),
        );
      } finally {
        inFlight.current = false;
      }
    };
    void poll(); // check immediately so a reload re-checks a `down` row at once
    const id = setInterval(() => void poll(), 4000);
    return () => clearInterval(id);
  }, [slug, unsettledSlugs, setPayload]);

  if (loading) return <PageSkeleton rows={4} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !payload) return null;
  const connections = payload.connections;

  if (!app.is_manager) {
    return (
      <ManagerOnlyPage
        app={app}
        title="Private connections"
        description="Let this app reach a private HTTP service running on your own server or VM."
      />
    );
  }

  async function createConnection(e: React.FormEvent) {
    e.preventDefault();
    if (!slug || !name || !origin) return;
    await runAction({
      do: () =>
        api.post<CreatedConnection>(`/api/apps/${encodeURIComponent(slug)}/connections`, {
          slug: name,
          origin,
        }),
      onSuccess: (res) => {
        setCreated(res);
        setName('');
        setOrigin('http://localhost:8080');
        return `Created ${res.internalHost}.`;
      },
      errorPrefix: "Couldn't create connection",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function deleteConnection(connSlug: string) {
    if (!slug) return;
    if (
      !confirm(
        `Delete connection "${connSlug}"?\n\n` +
          `• The app's this.connection("${connSlug}") calls will fail until you recreate it.\n` +
          `• This only removes the connection from Kin. The connector keeps running on the machine ` +
          `where you installed it — to remove it there afterward, run:\n\n` +
          `    ${CONNECTOR_UNINSTALL_CMD}`,
      )
    )
      return;
    // Clear any banners from a previous delete so a stale uninstall command
    // isn't shown while this new delete is in flight.
    setUninstall(null);
    setTeardownFailures(null);
    let result: { uninstallCommand: string; cfTeardownFailures?: string[] } | undefined;
    await runAction({
      do: () =>
        api.delete<{ uninstallCommand: string; cfTeardownFailures?: string[] }>(
          `/api/apps/${encodeURIComponent(slug)}/connections/${encodeURIComponent(connSlug)}`,
        ),
      onSuccess: (res) => {
        result = res;
        return `Deleted ${connSlug}.`;
      },
      errorPrefix: "Couldn't delete connection",
      setBusy,
      setMessage: setActionMessage,
    });
    if (!result) return;
    // Optimistic local removal — server already confirmed.
    setPayload((prev) => ({
      connections: prev.connections.filter((c) => c.slug !== connSlug),
    }));
    // Surface the uninstall command (so the VM can be cleaned) and any
    // Cloudflare-side teardown failures the operator must chase down.
    setUninstall(result.uninstallCommand);
    setTeardownFailures(result.cfTeardownFailures?.length ? result.cfTeardownFailures : null);
  }

  async function runTest(connSlug: string) {
    if (!slug) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post<ConnectionTestResult>(
        `/api/apps/${encodeURIComponent(slug)}/connections/${encodeURIComponent(connSlug)}/test`,
        { path: testPath || '/' },
      );
      setTestResult(r);
    } catch (err) {
      setTestResult({ error: 'network', message: apiMessage(err) });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Private connections"
        description="Let this app reach a private HTTP service running on your own server or VM."
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0 space-y-5">
          {actionMessage && (
            <Banner tone={actionMessage.tone} onDismiss={() => setActionMessage(null)}>
              {actionMessage.text}
            </Banner>
          )}

          {uninstall && (
            <Banner tone="info" onDismiss={() => setUninstall(null)}>
              <div className="space-y-1.5">
                <p>
                  Removed from Kin (the tunnel and its credentials are gone). The connector is still
                  running on your machine — to remove it, run this on the same machine where you
                  installed it:
                </p>
                <div className="flex items-start gap-2">
                  <Mono className="min-w-0 break-all">{uninstall}</Mono>
                  <CopyButton text={uninstall} />
                </div>
              </div>
            </Banner>
          )}

          {teardownFailures && (
            <Banner tone="warn" onDismiss={() => setTeardownFailures(null)}>
              <div>
                Cloudflare teardown didn't fully complete. You may need to remove these by hand:
                <ul className="mt-1 list-disc pl-5">
                  {teardownFailures.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            </Banner>
          )}

          <Card>
            <CardHeader
              title="New connection"
              description="Name it, point it at your local service's origin, then run the install command on that machine."
            />
            <form
              onSubmit={(e) => void createConnection(e)}
              className="px-5 py-4 grid sm:grid-cols-[200px_1fr_auto] gap-3 items-end"
            >
              <Field label="Name" required>
                <input
                  type="text"
                  required
                  pattern="[a-z][a-z0-9-]{1,30}[a-z0-9]"
                  value={name}
                  onChange={(e) => setName(e.target.value.toLowerCase())}
                  placeholder="warehouse"
                  className={`${inputClass} font-mono`}
                />
              </Field>
              <Field label="Origin" required>
                <input
                  type="text"
                  required
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="http://localhost:8080"
                  autoComplete="off"
                  className={`${inputClass} font-mono`}
                />
              </Field>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={busy}
                disabled={busy || name.length < 3 || !origin || !!created}
              >
                Create
              </Button>
            </form>
          </Card>

          {created && (
            <Card>
              <CardHeader
                title="Install command — copy it now"
                description="The token below is only displayed at creation."
              />
              <div className="px-5 py-4 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted">Assigned host</span>
                  <Mono className="font-medium">{created.internalHost}</Mono>
                </div>
                <div className="flex items-start gap-2">
                  <pre className="flex-1 min-w-0 overflow-x-auto rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs">
                    <code className="font-mono text-ink-2">{created.installCommand}</code>
                  </pre>
                  <CopyButton text={created.installCommand} />
                </div>
                <p className="text-xs text-muted">
                  Run this once on your server (as an admin). It's shown only now — recreate the
                  connection if you lose it.
                </p>
                <Button variant="ghost" size="sm" onClick={() => setCreated(null)}>
                  Dismiss
                </Button>
              </div>
            </Card>
          )}

          {connections.length === 0 ? (
            <EmptyState
              icon={<IconPlug size={18} />}
              title="No connections yet"
              description={
                <>
                  Create one above, then reach it from your app with{' '}
                  <Mono>await this.connection(&quot;name&quot;).fetch(&quot;/...&quot;)</Mono>.
                </>
              }
            />
          ) : (
            <Card>
              <CardHeader
                title="Connections"
                description={`${connections.length} connection${connections.length === 1 ? '' : 's'}`}
              />
              <ul className="divide-y divide-line">
                {connections.map((c) => {
                  const snippet = `await this.connection("${c.slug}").fetch("/...")`;
                  return (
                    <li key={c.slug} className="px-5 py-3.5 text-sm space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="min-w-0 inline-flex items-center gap-3">
                          <IconPlug size={14} className="text-faint shrink-0" />
                          <Mono className="font-medium">{c.slug}</Mono>
                          <span className="text-xs text-muted truncate">{c.internalHost}</span>
                          {c.origin && (
                            <span className="text-xs text-muted truncate">
                              → forwards to {c.origin}
                            </span>
                          )}
                        </span>
                        <span className="inline-flex items-center gap-3 shrink-0">
                          <StatusBadge status={c.status} />
                          {c.status === 'connected' && originOk[c.slug] === false && (
                            <span className="text-xs text-warn">service not responding</span>
                          )}
                          <span className="text-xs text-muted">seen {relTime(c.lastSeenAt)}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (testOpen === c.slug) {
                                setTestOpen(null);
                              } else {
                                setTestOpen(c.slug);
                                setTestResult(null);
                                setTestPath('/');
                              }
                            }}
                          >
                            Test
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void deleteConnection(c.slug)}
                            disabled={busy}
                            className="!text-danger hover:!bg-danger-soft"
                          >
                            <IconTrash size={13} />
                            Delete
                          </Button>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mono className="min-w-0 truncate">{snippet}</Mono>
                        {/* The button copies full wire-up instructions (not just the
                            one-liner shown) — paste them to the AI so it calls the
                            connection from the right place. */}
                        <CopyButton text={connectionWireup(c.slug)} label="Copy wire-up" />
                      </div>
                      {testOpen === c.slug && (
                        <div className="rounded-lg border border-line bg-surface-2 px-3 py-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted shrink-0">
                              GET https://{c.internalHost}
                            </span>
                            <input
                              className={`${inputClass} font-mono text-xs`}
                              value={testPath}
                              onChange={(e) => setTestPath(e.currentTarget.value)}
                              placeholder="/health"
                              aria-label="Request path"
                            />
                            <Button
                              variant="primary"
                              size="sm"
                              loading={testing}
                              onClick={() => void runTest(c.slug)}
                            >
                              Send GET
                            </Button>
                          </div>
                          {testResult &&
                            ('error' in testResult ? (
                              <Banner tone="error">
                                {testResult.error === 'timeout' ? 'Timed out' : 'Network error'}:{' '}
                                {testResult.message}
                              </Banner>
                            ) : (
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2 text-xs">
                                  <span
                                    className={`rounded px-1.5 py-0.5 font-mono ${testResult.ok ? 'bg-ok-soft text-ok' : 'bg-danger-soft text-danger'}`}
                                  >
                                    {testResult.status} {testResult.statusText}
                                  </span>
                                  <span className="text-muted">
                                    {testResult.contentType ?? 'no content-type'} ·{' '}
                                    {testResult.durationMs}ms
                                  </span>
                                </div>
                                <pre className="max-h-60 overflow-auto rounded border border-line bg-surface px-2 py-1.5 text-xs whitespace-pre-wrap break-all">
                                  {testResult.bodyPreview || '(empty body)'}
                                </pre>
                                {testResult.truncated && (
                                  <p className="text-xs text-muted">Response truncated to 16 KB.</p>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}
